import React, {useCallback, useState} from 'react';

import {useBeforeUnload} from '@optimaxdev/analytics/desktop/hooks/useBeforeUnload/useBeforeUnload';
import {connect} from 'react-redux';

import {Timer} from 'desktop/components/helpers/timer/timer';
import {ApplicationStoreType} from 'desktop/store';

import {disableUpsells} from '../../store/reducers/upsells/upsells';
import {
    getAddedUpgrades,
    getUpsellTimerState,
    getUpsellsState,
    getUpgrades,
} from '../../store/selectors/selectors';
import {UpsellType} from '../../types/upsellsTypes';
import {Notification} from '../notification/notification';
import {sendAnalytics} from '../orderInfoInTYP/helpers';
import {UpgradesList} from '../upgradesList/upgradesList';
import {UpsellsMessages} from '../upsellsMessages/upsellsMessages';

import s from './upsellBlock.scss';

type PropsType = {
    upgrades: Array<UpsellType>;
    lockUpsells: () => void;
    addedUpgrades: Array<UpsellType>;
    isUpsellsDisabled: boolean;
    endTime: number;
};

/**
 * UpsellBlock component
 * @param {PropsType} props - Component props
 * @return {React.ReactElement} component
 */
export const UpsellBlock: React.FC<PropsType> = ({
    addedUpgrades,
    endTime,
    isUpsellsDisabled,
    lockUpsells,
    upgrades,
}) => {
    const [viewedUpgrades, setViewedUpgrades] = useState<UpsellType[]>([]);

    useBeforeUnload(() => {
        const getItemPosition = (upsell: UpsellType) => {
            const accessoryIndex = upgrades.findIndex(({id}) => id === upsell.id);
            return accessoryIndex + 1;
        };
        if (viewedUpgrades.length >= 0) sendAnalytics(viewedUpgrades, getItemPosition, 'Upgrades');
    });

    const onTimerFinish = useCallback(() => {
        if (!isUpsellsDisabled) lockUpsells();
    }, [isUpsellsDisabled, lockUpsells]);

    const onView = useCallback((upsell: UpsellType) => {
        setViewedUpgrades(prevViewedUpgrades => [...prevViewedUpgrades, upsell]);
    }, []);

    return (
        <>
            <Notification />
            <UpsellsMessages upsells={addedUpgrades} />
            <div className={s.timerContainer}>
                <span className={s.onlyAvailiable}>
                    {isUpsellsDisabled ? 'Offer ended' : 'Only available for the next:'}
                </span>
                <Timer endTime={endTime} onFinish={onTimerFinish} />
            </div>
            <UpgradesList onView={onView} upgrades={upgrades} />
        </>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props, which contains isUpsellsDisabled, addedUpgrades, endTime from store
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    addedUpgrades: getAddedUpgrades(store),
    endTime: getUpsellTimerState(store),
    isUpsellsDisabled: getUpsellsState(store).isUpsellsDisabled,
    upgrades: getUpgrades(store),
});

const mapDispatchToProps = {lockUpsells: () => disableUpsells(true)};

export const UpsellBlockConnected = connect(mapStateToProps, mapDispatchToProps)(UpsellBlock);
